import { useEffect, useRef } from 'react';

export interface ShortcutDef {
  key: string;
  combo?: string; // e.g. 'g' for "g m" (lastKey=g, then key=m within window)
  handler: () => void;
  description: string;
}

const COMBO_WINDOW = 500; // ms

function isInputFocused(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if ((el as HTMLElement).isContentEditable) return true;
  return false;
}

export function useKeyboardShortcuts(shortcuts: ShortcutDef[]) {
  const lastKeyRef = useRef<{ key: string; time: number }>({ key: '', time: 0 });

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (isInputFocused()) return;

      const now = Date.now();
      const pressed = e.key.toLowerCase();

      for (const s of shortcuts) {
        if (s.combo) {
          // Combo shortcut: combo key must have been pressed recently
          if (
            pressed === s.key.toLowerCase() &&
            lastKeyRef.current.key === s.combo.toLowerCase() &&
            now - lastKeyRef.current.time < COMBO_WINDOW
          ) {
            e.preventDefault();
            s.handler();
            lastKeyRef.current = { key: '', time: 0 };
            return;
          }
        } else if (pressed === s.key.toLowerCase() && !e.ctrlKey && !e.metaKey && !e.altKey) {
          if (s.key === 'Escape') {
            s.handler();
            return;
          }
          e.preventDefault();
          s.handler();
          return;
        }
      }

      lastKeyRef.current = { key: pressed, time: now };
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [shortcuts]);
}
